import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { accountValidationApi, logoutApi } from '../services/authService';
import { setUser, clearUser, setLoading } from '../redux/slices/userSlice';
import { usePermissions } from './PermissionsContext';

const loginUrl = process.env.REACT_APP_SSO_REDIRECT;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const { setPermissionsFromAPI, clearPermissions } = usePermissions();

  const validateUser = async () => {
    try {
      dispatch(setLoading(true));
      const response = await accountValidationApi();
      // console.log('SSO validation response:', response.permissions);

      if (response && response.id) {
        setUserState(response);
        setIsAuthenticated(true);
        dispatch(setUser(response));

        // Extract and set permissions if they exist in the response
        if (response.permissions && typeof response.permissions === 'object') {
          // console.log('Setting permissions from validation API:', response.permissions);
          setPermissionsFromAPI(response.permissions);
        } else if (response.user_permissions && typeof response.user_permissions === 'object') {
          // console.log('Setting user permissions from validation API:', response.user_permissions);
          setPermissionsFromAPI(response.user_permissions);
        } else if (response.permissions && Array.isArray(response.permissions)) {
          // console.log('Setting permissions from validation API (array format):', response.permissions);
          setPermissionsFromAPI(response.permissions);
        } else if (response.user_permissions && Array.isArray(response.user_permissions)) {
          // console.log('Setting user permissions from validation API (array format):', response.user_permissions);
          setPermissionsFromAPI(response.user_permissions);
        } else {
          // console.log('No permissions found in validation response');
        }

        // console.log('User authenticated via SSO validation:', response);
      } else {
        // console.log('SSO validation failed - user not authenticated');
        setUserState(null);
        setIsAuthenticated(false);
        dispatch(clearUser());
      }
    } catch (error) {
      console.error('SSO validation error:', error);
      setUserState(null);
      setIsAuthenticated(false);
      dispatch(clearUser());
    } finally {
      setIsLoading(false);
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    if (window.location.pathname === '/auth/callback') {
      setIsLoading(false);
      dispatch(setLoading(false));
      validateUser();
      return;
    }
    validateUser();
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setIsLoading(true);
        validateUser();
      }
    };
    const onPopState = () => {
      setIsLoading(true);
      validateUser();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const clearAuth = () => {
    // console.log('Clearing SSO authentication state');
    setUserState(null);
    setIsAuthenticated(false);
    dispatch(clearUser());
    // Clear permissions as well
    clearPermissions();
  };

  const logout = async () => {
    const homeUrl = `${window.location.origin}/`;
    clearAuth();
    try {
      await logoutApi();
      window.location.href = loginUrl + "/logout?redirect=" + encodeURIComponent(homeUrl);
    } catch (error) {
      window.location.href = loginUrl + "/logout?redirect=" + encodeURIComponent(homeUrl);
      console.error('Logout API error:', error);
    }
    // Keep post-logout UX on LMS home page instead of SSO login page.
    // window.location.replace(homeUrl);
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    validateUser,
    logout,
    clearAuth,
    setUserState,
    setIsAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
